import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer } from 'vaul';
import { ensureUsdtSymbol } from '@/utils/symbols';

interface TickerSymbolDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (symbol: string) => void;
  title?: string;
  submitButtonText?: string;
}

export const TickerSymbolDrawer: React.FC<TickerSymbolDrawerProps> = ({
  open,
  onOpenChange,
  onSubmit,
  title = 'Enter Ticker Symbol',
  submitButtonText = 'Continue',
}) => {
  const [tickerInput, setTickerInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTickerInput('');
    }
  }, [open]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  }, []);

  const handleSubmit = useCallback(() => {
    const trimmedValue = tickerInput.trim();
    if (!trimmedValue) {
      return;
    }

    const normalizedSymbol = ensureUsdtSymbol(trimmedValue);
    onSubmit(normalizedSymbol);
    setTickerInput('');
  }, [tickerInput, onSubmit]);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/80 z-[120]" />
        <Drawer.Content
          className="bg-[#1a1b20] flex rounded-t-xl flex-col fixed bottom-0 left-0 right-0 max-h-[65vh] z-[121]"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
        >
          <div className="px-4 border-b border-gray-500/30 pb-3 pt-2 sticky top-0 bg-secondary z-10">
            <Drawer.Handle className="h-1 w-1/3 mt-0 mb-3 rounded-full" />
            <h2 className="text-md font-bold text-text-primary mb-4">{title}</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div>
              <label
                htmlFor="ticker-input"
                className="text-xs uppercase tracking-wide text-text-secondary mb-2 block"
              >
                Ticker (e.g., BTC, ETH, DOGE)
              </label>
              <input
                id="ticker-input"
                ref={inputRef}
                type="text"
                className="w-full bg-primary/40 border border-gray-600 text-text-primary rounded-lg p-3 text-sm focus:outline-none focus:border-accent uppercase"
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
                placeholder="BTC"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmit();
                  }
                }}
                onFocus={focusInput}
              />
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!tickerInput.trim()}
              className="w-full py-3 rounded-lg text-white font-semibold bg-accent hover:bg-accent/80 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitButtonText}
            </button>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

